import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import LogoMark from "@/components/LogoMark";
import { NAV_LINKS, BRAND } from "@/lib/site";

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [pathname]);

    return (
        <header
            data-testid="nav-header"
            className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "border-b border-white/10 bg-ink/85 backdrop-blur-xl"
                    : "bg-transparent"
            }`}
        >
            <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:h-20 sm:px-8">
                <Link
                    to="/"
                    data-testid="nav-logo-link"
                    className="flex items-center gap-2.5"
                >
                    <LogoMark size={34} />
                    <span className="leading-tight">
                        <span className="block font-display text-[13px] font-bold uppercase tracking-[0.12em] text-silver">
                            Checking In
                        </span>
                        <span className="block font-script text-[15px] text-neon-pink">
                            with Courtnie Baby Johnson
                        </span>
                    </span>
                </Link>

                <div className="hidden items-center gap-6 lg:flex">
                    {NAV_LINKS.slice(0, 6).map((link) => (
                        <NavLink
                            key={link.path}
                            to={link.path}
                            data-testid={`nav-link-${link.label
                                .toLowerCase()
                                .replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `text-[12px] font-medium uppercase tracking-[0.14em] transition-colors ${
                                    isActive
                                        ? "text-neon-pink"
                                        : "text-silver/75 hover:text-white"
                                }`
                            }
                        >
                            {link.label}
                        </NavLink>
                    ))}
                </div>

                <div className="hidden lg:block">
                    <Link
                        to="/contact"
                        data-testid="nav-cta-link"
                        className="rounded-full border border-neon-pink px-5 py-2 text-[12px] font-semibold uppercase tracking-[0.14em] text-neon-pink transition-all hover:bg-neon-pink hover:text-ink hover:neon-box-pink"
                    >
                        Check in
                    </Link>
                </div>

                <button
                    data-testid="nav-mobile-toggle"
                    onClick={() => setOpen(!open)}
                    aria-label="Toggle menu"
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-silver lg:hidden"
                >
                    {open ? <X size={18} /> : <Menu size={18} />}
                </button>
            </nav>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="nav-mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 top-16 z-40 overflow-y-auto border-t border-white/10 bg-ink lg:hidden"
                    >
                        <div className="flex flex-col px-5 py-8">
                            {NAV_LINKS.map((link, i) => (
                                <motion.div
                                    key={link.path}
                                    initial={{ opacity: 0, x: -16 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.05 * i, duration: 0.3 }}
                                >
                                    <NavLink
                                        to={link.path}
                                        className={({ isActive }) =>
                                            `block border-b border-white/10 py-4 font-display text-2xl font-bold uppercase ${
                                                isActive
                                                    ? "text-neon-pink"
                                                    : "text-silver"
                                            }`
                                        }
                                    >
                                        {link.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                            <p className="mt-8 font-script text-2xl text-neon-pink">
                                support local · support each other!
                            </p>
                            <p className="mt-2 text-xs text-fog">
                                {BRAND.email}
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
